import React, { useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CustomPropertyStatusField from '../CustomPropertyStatusField';
import { LISTING_STATUS_LABELS } from '../listingDisplayStatus';
import { formatDaysOnMarket } from '../ListingsSummarySection';
import {
  computePreviewDaysOnMarket,
  parseDateInput,
  requiresOffMarketDate,
} from '../listingMarketDates';
import ListingDateField from './ListingDateField';
import { LISTING_MODAL_BODY_SX, LISTING_MODAL_HELPER_SX } from './listingModalGlassStyles';

/**
 * Manage-listing status fields — cosmetic shell over the same manual/sync contract
 * as ListingStatusFields. Used only by ListingManageModal for now; other modals can
 * adopt this component later.
 */
const ListingManageStatusFields = ({
  compact = false,
  mlsSyncEnabled = false,
  mlsStatus = 'unknown',
  customPropertyStatus = '',
  onCustomPropertyStatusChange,
  onMarketDate = '',
  onMarketDateSource = null,
  onOffMarketDate = '',
  onOnMarketDateChange,
  onOffMarketDateChange,
  disabled = false,
  requireOnMarketDate = false,
  showDaysPreview = false,
}) => {
  const manualDisabled = disabled || mlsSyncEnabled;
  const statusForOffMarket = mlsSyncEnabled ? mlsStatus : customPropertyStatus;
  const showOffMarketDate = requiresOffMarketDate(statusForOffMarket);
  const [openDateField, setOpenDateField] = useState(null);

  const previewDays = useMemo(() => {
    if (!showDaysPreview || mlsSyncEnabled) return null;
    const onIso = parseDateInput(onMarketDate);
    const offIso = showOffMarketDate ? parseDateInput(onOffMarketDate) : null;
    if (!onIso) return null;
    return computePreviewDaysOnMarket({
      onMarketDate: onIso,
      offMarketDate: offIso,
      customPropertyStatus: customPropertyStatus || null,
    });
  }, [
    showDaysPreview,
    mlsSyncEnabled,
    onMarketDate,
    onOffMarketDate,
    customPropertyStatus,
    showOffMarketDate,
  ]);

  const automaticStatusLabel =
    LISTING_STATUS_LABELS[mlsStatus] || LISTING_STATUS_LABELS.unknown;

  // Under auto sync the date looks authoritative whether or not it is, and days
  // on market is only as good as this date. Say which one the reader is seeing.
  const onMarketDateOrigin = !onMarketDate
    ? undefined
    : onMarketDateSource === 'slapi'
      ? 'From your listing site.'
      : 'Entered by hand — your listing site has not confirmed this date.';

  if (mlsSyncEnabled) {
    return (
      <Stack spacing={compact ? 1.5 : 2}>
        {!compact ? (
          <Typography sx={LISTING_MODAL_HELPER_SX}>
            These update on their own. Switch to Manual to override.
          </Typography>
        ) : null}

        <Box sx={{ opacity: 0.72 }}>
          <Typography
            component="label"
            sx={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 500,
              color: 'rgba(0,0,0,0.45)',
              mb: 0.75,
            }}
          >
            Property status
          </Typography>
          <Box
            sx={{
              borderRadius: '14px',
              backgroundColor: 'rgba(0,0,0,0.03)',
              px: 1.75,
              py: 1.35,
              minHeight: 48,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontSize: '16px', color: 'rgba(0,0,0,0.62)' }}>
              {automaticStatusLabel}
            </Typography>
          </Box>
        </Box>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2}>
            <ListingDateField
              label="On-market date"
              value={onMarketDate}
              disabled
              readOnly
              helperText={onMarketDateOrigin}
            />
            {showOffMarketDate || onOffMarketDate ? (
              <ListingDateField
                label="Off-market date"
                value={onOffMarketDate}
                disabled
                readOnly
              />
            ) : null}
          </Stack>
        </LocalizationProvider>
      </Stack>
    );
  }

  return (
    <Stack spacing={compact ? 1.5 : 2}>
      {!compact ? (
        <Typography sx={LISTING_MODAL_BODY_SX}>
          {requireOnMarketDate
            ? 'Set the property status and on-market date below.'
            : 'You can add a property status now, or come back later from Manage listing.'}
        </Typography>
      ) : null}

      <CustomPropertyStatusField
        value={customPropertyStatus}
        onChange={onCustomPropertyStatusChange}
        disabled={manualDisabled}
        label="Property status"
      />

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Stack spacing={2}>
          <ListingDateField
            label="On-market date"
            value={onMarketDate}
            onChange={onOnMarketDateChange}
            disabled={manualDisabled}
            required={requireOnMarketDate}
            open={openDateField === 'on'}
            onOpenChange={(next) => setOpenDateField(next ? 'on' : null)}
            helperText={
              compact
                ? undefined
                : requireOnMarketDate
                  ? 'Required for days on market.'
                  : 'Optional now — required when you save the status later.'
            }
          />

          {showOffMarketDate ? (
            <ListingDateField
              label="Off-market date"
              value={onOffMarketDate}
              onChange={onOffMarketDateChange}
              disabled={manualDisabled}
              required
              open={openDateField === 'off'}
              onOpenChange={(next) => setOpenDateField(next ? 'off' : null)}
              helperText={compact ? undefined : 'Required for under contract or sold.'}
            />
          ) : null}
        </Stack>
      </LocalizationProvider>

      {previewDays != null ? (
        <Typography sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.55)' }}>
          Days on market:{' '}
          <Typography component="span" sx={{ fontWeight: 600, color: 'rgba(0,0,0,0.78)' }}>
            {formatDaysOnMarket(previewDays)}
          </Typography>
          {!compact ? (
            <>
              {' — '}
              <Typography component="span" sx={{ color: 'rgba(0,0,0,0.48)' }}>
                counted from on-market date
                {showOffMarketDate && onOffMarketDate.trim() ? ', stopping at off-market date' : ''}
              </Typography>
            </>
          ) : null}
        </Typography>
      ) : null}
    </Stack>
  );
};

export default ListingManageStatusFields;
